import { ART_PALETTE } from '../art-palette.js';
import { blankPixels, plainTile } from './helpers.js';

/**
 * Floors — flat hybrid house style (docs/art-direction-rules.md §0).
 *
 * Floors are *connected terrain*: they carry no Ink contour and no full-plane
 * grout grid, because either one draws the 16px cell edge onto the map. The
 * structure a floor is allowed to show is:
 *
 * - plank/brick courses whose period divides 16 (4 or 8px), with a bond that
 *   also repeats inside the cell, so the rhythm never lets the eye find the
 *   cell boundary;
 * - stone plates from a wrap-around lattice (`stonePlates`), which continue
 *   across every self-join instead of terminating at it;
 * - flat fields with, at most, a ±1 ramp step accent.
 *
 * `tile` and `checkered` are the two documented exceptions
 * (docs/intentional-seam-exceptions.md): a regular grid is the design there, so
 * they keep it and only hold the contrast down.
 */

/** Integer hash in [0,1). `(x * a + y * b) % m` bands diagonally; this does not. */
function hash(x, y) {
  let v = Math.imul(x, 0x1f123bb5) ^ Math.imul(y, 0x5f356495);
  v ^= v >>> 15; v = Math.imul(v, 0x2c1b3c6d); v ^= v >>> 12;
  return (v >>> 0) / 0x100000000;
}

/**
 * Irregular stone plates on a staggered lattice, computed with wrap-around
 * distance so every plate continues across the tile edge into its own copy.
 * Returns plate ids per pixel plus a 1px joint mask (a pixel is a joint when the
 * plate to its east or south differs).
 */
function stonePlates({ step, jitter, salt = 0, round = true, keep = 1, offset = true }) {
  const seeds = [];
  for (let row = 0, sy = 0; sy < 16; sy += step, row++) {
    for (let sx = 0; sx < 16; sx += step) {
      // Alternate rows can be offset half a step: hexagonal packing suits few
      // large plates, but at cobble scale it drifts into diagonal stripes, so
      // small stones jitter off a square lattice instead.
      // Dropping lattice points (`keep`) merges neighbours into bigger stones,
      // which is the only way to get sizes between two lattice steps.
      if (hash(sx + 7, sy + salt + 3) > keep) continue;
      const baseX = sx + (offset ? (row % 2) * (step / 2) : 0);
      const jx = Math.round((hash(sx + salt, sy + 5) - 0.5) * 2 * jitter);
      const jy = Math.round((hash(sy + 11, sx + salt) - 0.5) * 2 * jitter);
      seeds.push([((baseX + jx) % 16 + 16) % 16, ((sy + jy) % 16 + 16) % 16]);
    }
  }
  const wrapped = (a, b) => {
    const d = Math.abs(a - b) % 16;
    return d > 8 ? 16 - d : d;
  };
  const plate = Array.from({ length: 16 }, () => Array(16).fill(0));
  for (let y = 0; y < 16; y++) for (let x = 0; x < 16; x++) {
    let best = 0; let bestDistance = Infinity;
    for (let index = 0; index < seeds.length; index++) {
      const dx = wrapped(x, seeds[index][0]);
      const dy = wrapped(y, seeds[index][1]);
      const distance = dx * dx + dy * dy;
      if (distance < bestDistance) { bestDistance = distance; best = index; }
    }
    plate[y][x] = best;
  }
  // Mortar sits on the low side of each boundary. Taking the diagonal neighbour
  // too rounds the plate corners off — cobbles want that, slabs do not.
  const joint = Array.from({ length: 16 }, (_, y) => Array.from({ length: 16 }, (_, x) =>
    plate[y][x] !== plate[y][(x + 1) % 16]
    || plate[y][x] !== plate[(y + 1) % 16][x]
    || (round && plate[y][x] !== plate[(y + 1) % 16][(x + 1) % 16])));
  // A plate that owns any outer pixel continues into the neighbouring tile, so
  // its colour has to be identical in every random variant or a stone changes
  // value halfway across. Plates that stop short of the edge may re-tone.
  const spansEdge = seeds.map(() => false);
  for (let i = 0; i < 16; i++) {
    for (const id of [plate[0][i], plate[15][i], plate[i][0], plate[i][15]]) spansEdge[id] = true;
  }
  return { plate, joint, seeds, spansEdge };
}

/**
 * Horizontal courses with staggered butt joints (§0 motif table).
 * `joints[course]` lists the butt-joint columns for that course; no two
 * neighbouring courses share one, so the bond never lines up into a column.
 */
function courseFloor({ courseHeight, joints, field, separator, buttJoint, tone, phase = 1 }) {
  const pixels = blankPixels(field);
  for (let y = 0; y < 16; y++) {
    // `phase` slides the courses so that rows 15 and 0 are both face, never
    // face-against-separator. The seam then matches exactly on its outermost
    // row, which is what the depth-weighted seam score reads first.
    const row = (y + phase) % 16;
    const course = Math.floor(row / courseHeight);
    const columns = joints[course % joints.length];
    const onSeparator = row % courseHeight === courseHeight - 1;
    for (let x = 0; x < 16; x++) {
      if (onSeparator) { pixels[y][x] = separator; continue; }
      // Which board/brick of this course owns x. Columns left of the first
      // joint belong to the last piece: that board wraps through the tile edge,
      // so both halves must take the same tone or the seam becomes visible.
      // A course with no joint is one unbroken board running off both sides.
      let piece = Math.max(0, columns.length - 1);
      for (let index = 0; index < columns.length; index++) if (x >= columns[index]) piece = index;
      pixels[y][x] = tone(hash(course * 37 + 3, piece * 61 + 7), course, piece);
      if (columns.includes(x)) pixels[y][x] = buttJoint;
    }
  }
  return pixels;
}

/** Five cobble variants: shared plate geometry, re-toned interior stones. */
function cobblestoneVariants() {
  const { plate, joint, seeds, spansEdge } = stonePlates({
    step: 4, jitter: 2, keep: 0.6, salt: 3, round: false, offset: false,
  });
  return Array.from({ length: 5 }, (_, variant) => {
    // Plates that wrap into the next tile keep one fixed tone across variants;
    // only fully interior stones re-tone, so any variant abuts any other.
    // Stone faces stay above the mortar value, so the joints read as a network
    // instead of dissolving into the stones.
    const tone = seeds.map(([sx, sy], id) => {
      const t = spansEdge[id] ? hash(sx * 3 + 5, sy * 7 + 11) : hash(sx * 3 + 5, sy * 7 + variant * 97 + 11);
      return t > 0.62 ? 27 : 26;
    });
    const pixels = blankPixels(26);
    for (let y = 0; y < 16; y++) for (let x = 0; x < 16; x++) {
      pixels[y][x] = joint[y][x] ? 25 : tone[plate[y][x]];
    }
    return pixels;
  });
}

const COBBLESTONE_FLOOR = {
  key: 'outdoor-floor.cobblestone', name: 'Grey cobblestone floor', channel: 'outdoorFloor', mode: 'random',
  randomVariantCount: 5, paletteSection: 'outdoorFloors', palette: ART_PALETTE,
  collision: { ground: false }, components: { ground: Object.fromEntries(cobblestoneVariants().map((pixels, index) => [index, pixels])) },
};

export function floorPixels(kind) {
  if (kind === 'wood') {
    // Long boards, not a bond. Boards run off both edges unbroken and there are
    // deliberately no butt joints: on a single 16×16 plain tile every joint
    // recurs on the 16px pitch and stacks into a column of ticks, which is what
    // made this floor read as orange brickwork. Grain carries the wood instead.
    const pixels = blankPixels(8);
    // Separator every 4px, one ramp step under the board face. Rows 2/6/10/14
    // means a board straddles the cell edge, so rows 15 and 0 are the same face.
    for (let y = 2; y < 16; y += 4) for (let x = 0; x < 16; x++) pixels[y][x] = 7;

    // Two Sand streaks per board, hash-placed: fixed density, uneven spacing.
    // They sit on row `cy`, the middle of the 3px board, so grain never thickens
    // a separator into a 2px line, and they are always lighter than the board —
    // a dark streak down a board middle reads as a second separator.
    for (let cy = 0; cy < 16; cy += 4) for (let n = 0; n < 2; n++) {
      const x = Math.floor(hash(cy * 7 + n * 29 + 3, 11) * 16);
      // Streaks, not ticks: a 1px mark reads as a brick head joint, a 2–4px one
      // reads as grain running with the board.
      const length = 2 + Math.floor(hash(n * 13 + 5, cy * 3 + 7) * 3);
      for (let i = 0; i < length; i++) pixels[cy][(x + i) % 16] = 9;
    }
    return pixels;
  }
  if (kind === 'stone') {
    // Cool ashlar: 16×8 blocks in a half-lap bond, mortar one step down from
    // the face so the bond reads without printing a grid. Faces are one flat
    // value — toning individual blocks puts a lighter block on the 16px pitch,
    // which is a lattice the eye locks onto immediately.
    return courseFloor({
      courseHeight: 8,
      joints: [[5], [13]],
      field: 26,
      separator: 25,
      buttJoint: 25,
      tone: () => 26,
    });
  }
  if (kind === 'tile') {
    // Documented exception (docs/intentional-seam-exceptions.md §2): a regular
    // 8×8 grid is the design. Single-edge grout so the neighbour supplies the
    // missing line; grout moved to Stone dark (25) so it no longer shouts.
    const pixels = blankPixels(26);
    for (let y = 0; y < 16; y++) for (let x = 0; x < 16; x++) {
      if (x % 8 === 0 || y % 8 === 0) pixels[y][x] = 25;
      else if (x % 8 === 1 || y % 8 === 1) pixels[y][x] = 27;
    }
    return pixels;
  }
  if (kind === 'carpet' || kind === 'carpet-blue' || kind === 'carpet-green') {
    // Flat pile field plus a woven binding lattice one step lighter. The lattice
    // period is 8, so it reads as rug weave and never as the tile boundary.
    const [base, weave] = kind === 'carpet'
      ? [30, 28]
      : kind === 'carpet-blue'
        ? [29, 25]
        : [13, 14];
    const pixels = blankPixels(base);
    for (let y = 0; y < 16; y++) for (let x = 0; x < 16; x++) {
      // Two offset rows of 2px binding dashes per 8px — enough to say "woven",
      // far too little to count as texturing the field.
      if (y % 8 === 3 && x % 8 < 2) pixels[y][x] = weave;
      else if (y % 8 === 7 && (x + 4) % 8 < 2) pixels[y][x] = weave;
    }
    return pixels;
  }
  if (kind === 'concrete') {
    // One flat slab. Aggregate is a sparse ±1 step speck cluster on a jittered
    // lattice — no per-pixel formula, no visible density change at the seam.
    const pixels = blankPixels(26);
    for (let cy = 0; cy < 16; cy += 4) for (let cx = 0; cx < 16; cx += 4) {
      const t = hash(cx + 3, cy + 9);
      if (t < 0.45) continue;
      const x = (cx + Math.floor(hash(cx, cy) * 4)) % 16;
      const y = (cy + Math.floor(hash(cy + 7, cx) * 4)) % 16;
      pixels[y][x] = t > 0.78 ? 27 : 25;
    }
    return pixels;
  }
  if (kind === 'brick') {
    // Warm pavers on the cliff ramp, drawn like the living-room reference: one
    // flat brick value, light mortar rather than inked joints, and a plain
    // two-course running bond. Both the course and the bond repeat every 8px,
    // half the cell, so there is no rhythm that can mark the 16px boundary —
    // and no scattered dark bricks, which repeat into a legible super-pattern.
    return courseFloor({
      courseHeight: 4,
      joints: [[2, 10], [6, 14]],
      field: 5,
      separator: 6,
      buttJoint: 6,
      tone: () => 5,
    });
  }
  if (kind === 'checkered') {
    // Documented exception (docs/intentional-seam-exceptions.md §1): the seam is
    // the checker. Cream against Stone (26) instead of Stone shadow (24) so the
    // lattice stays a floor rather than a chessboard.
    const pixels = blankPixels(11);
    for (let y = 0; y < 16; y++) for (let x = 0; x < 16; x++) {
      pixels[y][x] = (Math.floor(x / 8) + Math.floor(y / 8)) % 2 === 1 ? 26 : 11;
    }
    return pixels;
  }
  if (kind === 'flagstone') {
    // Big irregular slabs: a low `keep` merges lattice cells into plates wider
    // than the lattice step, and square corners keep them cut rather than
    // tumbled. The faces are one flat value on purpose — a lighter slab recurs
    // on the 16px pitch and is the single thing that makes the cell findable.
    const { joint } = stonePlates({
      step: 4, jitter: 2, keep: 0.35, salt: 29, round: false, offset: false,
    });
    const pixels = blankPixels(26);
    for (let y = 0; y < 16; y++) for (let x = 0; x < 16; x++) {
      if (joint[y][x]) pixels[y][x] = 25;
    }
    return pixels;
  }
  throw new Error(`Unknown floor kind: ${kind}`);
}

function outdoorFloorTile(kind, name) {
  return plainTile({
    key: `outdoor-floor.${kind}`,
    name,
    channel: 'outdoorFloor',
    section: 'outdoorFloors',
    component: 'ground',
    pixels: floorPixels(kind),
    collision: { ground: false },
  });
}

export const OUTDOOR_FLOOR_TILES = [
  COBBLESTONE_FLOOR,
  outdoorFloorTile('concrete', 'Concrete path'),
  outdoorFloorTile('brick', 'Brick pavers'),
  outdoorFloorTile('wood', 'Wooden deck'),
  outdoorFloorTile('flagstone', 'Flagstone path'),
  outdoorFloorTile('tile', 'Stone tile path'),
];

export const INTERIOR_FLOOR_TILES = [
  ['wood', 'Wood floor'],
  ['stone', 'Stone floor'],
  ['tile', 'Tile floor'],
  ['carpet', 'Red carpet'],
  ['carpet-blue', 'Blue carpet'],
  ['carpet-green', 'Green carpet'],
  ['concrete', 'Concrete floor'],
  ['brick', 'Brick floor'],
  ['checkered', 'Checkered floor'],
  ['flagstone', 'Flagstone floor'],
].map(([kind, name]) => plainTile({
  key: `interior-floor.${kind}`,
  name,
  channel: 'interiorFloor',
  section: 'interiorFloors',
  component: 'ground',
  pixels: floorPixels(kind),
}));

export { COBBLESTONE_FLOOR };
